import React, { useMemo, useRef, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Dialog,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Chip,
  Collapse,
  Avatar,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNotifier } from "../../../Core/Notifier";
import { useCreateDriverMutation } from "../../../Api/attoDeskApi";
import { uploadImage } from "../../../Components/UploadFile/upload";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { IDriver } from "../../../Api/Interface/api.interface";

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimeSlot {
  startTime: string;
  endTime: string;
  hourlySalary: string;
}

interface Availability {
  day: string;
  timeSlots: TimeSlot[];
}

interface IDriverpopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IDriver;
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#a5b4fc" },
    "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { fontSize: 13.5 },
  "& .MuiSelect-select": { fontSize: 13.5 },
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0.4 },
};

const labelSx = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "#475569",
  mb: 0.75,
  display: "block",
} as const;

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  p: 2.5,
  mb: 2,
} as const;

const sectionHeadingSx = {
  fontSize: 11,
  fontWeight: 700,
  color: "#94a3b8",
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  mb: 2,
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  manager: "Manager",
  driver: "Driver",
};

// ─── Component ────────────────────────────────────────────────────────────────
const Driver = ({ openModel = false, handleCloseDialog, data }: IDriverpopup) => {
  const [open] = React.useState(openModel);
  const { showErrorMessage, showMessage } = useNotifier();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
    const s = new Set<string>();
    (data?.availability || []).forEach((day: any) => {
      if (day.timeSlots?.some((sl: any) => sl.startTime || sl.endTime)) {
        s.add(day.day);
      }
    });
    return s;
  });

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [newCompany, { isLoading }] = useCreateDriverMutation();

  const handleClose = () => handleCloseDialog(false);
  const handleClickShowPassword = () => setShowPassword((p) => !p);
  const triggerAvatarUpload = () => avatarInputRef.current?.click();
  const toggleDay = (day: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });

  // ─── Formik ───────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      id: data?.id,
      driverMobileNum:    data?.driverMobileNum    || "",
      driverFirstName:    data?.driverFirstName    || "",
      driverLastName:     data?.driverLastName     || "",
      driverAltMobileNum: data?.driverAltMobileNum || "",
      driverImg:          data?.driverImg          || "",
      driverCity:         data?.driverCity         || "",
      driverAddress:      data?.driverAddress      || "",
      driverEmail:        data?.driverEmail        || "",
      driverUserRole:     data?.driverUserRole     || "",
      driverUserName:     data?.driverUserName     || "",
      driverPassWord:     "",
      driverDescription:  data?.driverDescription  || "",
      availability: data?.availability?.map((available) => ({
        day: available.day,
        timeSlots: available.timeSlots?.map((slot) => ({
          startTime:    slot.startTime    || "",
          endTime:      slot.endTime      || "",
          hourlySalary: slot.hourlySalary || "",
        })) || [{ startTime: "", endTime: "", hourlySalary: "" }],
      })) || [
        { day: "Sunday",    timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Monday",    timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Tuesday",   timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Wednesday", timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Thursday",  timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Friday",    timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
        { day: "Saturday",  timeSlots: [{ startTime: "", endTime: "", hourlySalary: "" }] },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.driverFirstName)  errors.driverFirstName  = "First Name is required";
      if (!values.driverMobileNum)  errors.driverMobileNum  = "Mobile Number is required";
      if (!values.driverUserRole)   errors.driverUserRole   = "User Role is required";
      if (!values.driverUserName)   errors.driverUserName   = "Username is required";
      if (!data && !values.driverPassWord) errors.driverPassWord = "Password is required";
      if (data && showChangePassword && !values.driverPassWord) errors.driverPassWord = "New password is required";
      const emailError     = validateEmail(values.driverEmail);
      if (emailError)  errors.driverEmail     = emailError;
      const mobileError    = validatePhoneNumber(values.driverMobileNum);
      if (mobileError) errors.driverMobileNum = mobileError;
      const altMobileError = validatePhoneNumber(values.driverAltMobileNum);
      if (altMobileError) errors.driverAltMobileNum = altMobileError;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let driverImg: string | undefined = data?.driverImg;
        if (selectedImage) {
          setIsImageUploading(true);
          driverImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const updatedData = {
          id:                 values.id,
          driverMobileNum:    values.driverMobileNum,
          driverAltMobileNum: values.driverAltMobileNum,
          driverFirstName:    values.driverFirstName,
          driverLastName:     values.driverLastName,
          driverCity:         values.driverCity,
          driverAddress:      values.driverAddress,
          driverEmail:        values.driverEmail,
          driverUserRole:     values.driverUserRole,
          driverDescription:  values.driverDescription,
          driverImg,
          availability:       values.availability,
          driverUserName:     values.driverUserName,
          driverPassWord:     values.driverPassWord,
        };
        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
          setSelectedImage(undefined);
        }
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Something went wrong");
      }
    },
  });

  // ─── Image resize ─────────────────────────────────────────────────────────
  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          let w = img.width, h = img.height;
          if (w > h) { if (w > maxW) { h *= maxW / w; w = maxW; } }
          else       { if (h > maxH) { w *= maxH / h; h = maxH; } }
          canvas.width = w; canvas.height = h;
          ctx?.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error("Canvas empty")),
            file.type,
          );
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { setSelectedImage(undefined); return; }
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    if (!validTypes.includes(file.type)) {
      showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
      return;
    }
    if (file.size > 2048 * 1024) {
      showErrorMessage("The file must be smaller than 2 MB.");
      return;
    }
    try {
      setSelectedImage(await resizeImage(file, 500, 500));
    } catch {
      showErrorMessage("Failed to resize the image.");
    }
  };

  // ─── Derived display values ───────────────────────────────────────────────
  const displayName = [formik.values.driverFirstName, formik.values.driverLastName]
    .filter(Boolean).join(" ") || "—";
  const initials = [formik.values.driverFirstName?.[0], formik.values.driverLastName?.[0]]
    .filter(Boolean).join("").toUpperCase() || "?";
  const avatarSrc = selectedImage
    ? URL.createObjectURL(selectedImage)
    : data?.driverImg
    ? `${LARAVEL_API_URL}/${data.driverImg}`
    : undefined;

  // ─── Weekly stats ─────────────────────────────────────────────────────────
  const weeklyStats = useMemo(() => {
    let totalMins = 0;
    let totalEarnings = 0;
    formik.values.availability.forEach((day) => {
      day.timeSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          const [sh, sm] = slot.startTime.split(":").map(Number);
          const [eh, em] = slot.endTime.split(":").map(Number);
          const diff = eh * 60 + em - (sh * 60 + sm);
          if (diff > 0) {
            totalMins += diff;
            totalEarnings += (diff / 60) * (parseFloat(slot.hourlySalary) || 0);
          }
        }
      });
    });
    return {
      hours:    Math.round((totalMins / 60) * 10) / 10,
      earnings: Math.round(totalEarnings * 100) / 100,
    };
  }, [formik.values.availability]);

  const busy = isLoading || isImageUploading;

  // ─── Tab definitions ──────────────────────────────────────────────────────
  const TABS = [
    { label: "Profile",  Icon: PersonOutlineIcon },
    { label: "Contact",  Icon: PhoneOutlinedIcon },
    { label: "Schedule", Icon: EventAvailableOutlinedIcon },
    { label: "Notes",    Icon: NotesOutlinedIcon },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >

          {/* ── Modal Header ──────────────────────────────────────────────── */}
          <Box sx={{
            px: 3, py: 2, bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex", alignItems: "center", gap: 2, flexShrink: 0,
          }}>
            {/* Avatar */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                src={avatarSrc}
                sx={{ width: 52, height: 52, bgcolor: "#2f3891", fontSize: 20, fontWeight: 700, cursor: "pointer" }}
                onClick={triggerAvatarUpload}
              >
                {!avatarSrc && initials}
              </Avatar>
              <Box
                onClick={triggerAvatarUpload}
                sx={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 22, height: 22, borderRadius: "50%",
                  bgcolor: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", border: "2.5px solid #fff",
                  "&:hover": { bgcolor: "#2f3891" },
                  transition: "background 0.15s",
                }}
              >
                <CameraAltIcon sx={{ fontSize: 11, color: "#fff" }} />
              </Box>
              <input
                ref={avatarInputRef}
                accept="image/*"
                style={{ display: "none" }}
                type="file"
                onChange={handleImageChange}
              />
            </Box>

            {/* Name + role */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.35 }}>
                {formik.values.driverUserRole && (
                  <Chip
                    label={ROLE_LABELS[formik.values.driverUserRole] || formik.values.driverUserRole}
                    size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#eef2ff", color: "#2f3891", border: "1px solid #c7d2fe" }}
                  />
                )}
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>Edit Profile</Typography>
              </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                onClick={handleClose}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" }, px: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={busy ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
                onClick={() => formik.handleSubmit()}
                disabled={busy}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" }, px: 2.5, boxShadow: "none" }}
              >
                {busy ? "Saving…" : "Save Changes"}
              </Button>
            </Box>
          </Box>

          {/* ── Tabs bar ──────────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                px: 1, minHeight: 44,
                "& .MuiTab-root": {
                  minHeight: 44, fontSize: 12.5, fontWeight: 600,
                  textTransform: "none", color: "#64748b",
                  gap: 0.6, px: 2, py: 0,
                },
                "& .Mui-selected": { color: "#2f3891" },
                "& .MuiTabs-indicator": { bgcolor: "#2f3891", height: 2.5, borderRadius: "2px 2px 0 0" },
              }}
            >
              {TABS.map(({ label, Icon }, i) => (
                <Tab key={i} label={label} icon={<Icon sx={{ fontSize: 15 }} />} iconPosition="start" />
              ))}
            </Tabs>
          </Box>

          {/* ── Scrollable content area ───────────────────────────────────── */}
          <Box sx={{
            flex: 1, overflow: "auto", bgcolor: "#f8fafc", p: 2.5,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 4 },
          }}>

            {/* ════ TAB 0 — Profile ════ */}
            {activeTab === 0 && (
              <Box>
                {/* Photo upload card */}
                <Box sx={{ ...cardSx, display: "flex", alignItems: "center", gap: 2.5 }}>
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar
                      src={avatarSrc}
                      sx={{ width: 84, height: 84, bgcolor: "#2f3891", fontSize: 28, fontWeight: 700, border: "3px solid #e2e8f0", cursor: "pointer" }}
                      onClick={triggerAvatarUpload}
                    >
                      {!avatarSrc && initials}
                    </Avatar>
                    <Box
                      onClick={triggerAvatarUpload}
                      sx={{
                        position: "absolute", bottom: 1, right: 1,
                        width: 26, height: 26, borderRadius: "50%",
                        bgcolor: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", border: "2px solid #fff",
                        "&:hover": { bgcolor: "#2f3891" }, transition: "background 0.15s",
                      }}
                    >
                      <CameraAltIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#0f172a" }}>Profile Photo</Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.4, lineHeight: 1.5 }}>
                      Click the avatar to upload. JPEG, PNG, GIF · Max 2 MB.
                    </Typography>
                    {selectedImage && (
                      <Chip
                        label="New photo selected"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 13, color: "#16a34a !important" }} />}
                        sx={{ mt: 0.75, height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Identity */}
                <Box sx={cardSx}>
                  <Typography sx={sectionHeadingSx}>Identity</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={labelSx}>First Name *</Typography>
                      <TextField fullWidth size="small" placeholder="Enter first name"
                        {...formik.getFieldProps("driverFirstName")} sx={fieldSx}
                        error={formik.touched.driverFirstName && Boolean(formik.errors.driverFirstName)}
                        helperText={formik.touched.driverFirstName && formik.errors.driverFirstName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={labelSx}>Last Name</Typography>
                      <TextField fullWidth size="small" placeholder="Enter last name"
                        {...formik.getFieldProps("driverLastName")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={labelSx}>Role *</Typography>
                      <TextField fullWidth select size="small"
                        {...formik.getFieldProps("driverUserRole")} sx={fieldSx}
                        error={formik.touched.driverUserRole && Boolean(formik.errors.driverUserRole)}
                        helperText={formik.touched.driverUserRole && formik.errors.driverUserRole}>
                        <MenuItem value="super_admin">Super Admin</MenuItem>
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="driver">Driver</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>

                {/* Credentials */}
                <Box sx={cardSx}>
                  <Typography sx={sectionHeadingSx}>Login Credentials</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={labelSx}>Username *</Typography>
                      <TextField fullWidth size="small" placeholder="Enter username"
                        {...formik.getFieldProps("driverUserName")} sx={fieldSx}
                        error={formik.touched.driverUserName && Boolean(formik.errors.driverUserName)}
                        helperText={formik.touched.driverUserName && formik.errors.driverUserName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {data ? (
                        showChangePassword ? (
                          <>
                            <Typography sx={labelSx}>New Password</Typography>
                            <TextField fullWidth size="small" placeholder="Enter new password"
                              type={showPassword ? "text" : "password"}
                              {...formik.getFieldProps("driverPassWord")} sx={fieldSx}
                              error={formik.touched.driverPassWord && Boolean(formik.errors.driverPassWord)}
                              helperText={formik.touched.driverPassWord && formik.errors.driverPassWord}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                                      {showPassword ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }} />
                            <Button size="small" onClick={() => { setShowChangePassword(false); formik.setFieldValue("driverPassWord", ""); }}
                              sx={{ mt: 0.5, fontSize: 11, textTransform: "none", color: "#94a3b8", p: 0 }}>
                              Cancel password change
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography sx={labelSx}>Password</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowChangePassword(true)}
                              sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12.5, borderColor: "#e2e8f0", color: "#475569" }}>
                              Change Password
                            </Button>
                          </>
                        )
                      ) : (
                        <>
                          <Typography sx={labelSx}>Password *</Typography>
                          <TextField fullWidth size="small" placeholder="Enter password"
                            type={showPassword ? "text" : "password"}
                            {...formik.getFieldProps("driverPassWord")} sx={fieldSx}
                            error={formik.touched.driverPassWord && Boolean(formik.errors.driverPassWord)}
                            helperText={formik.touched.driverPassWord && formik.errors.driverPassWord}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                                    {showPassword ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }} />
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {/* ════ TAB 1 — Contact ════ */}
            {activeTab === 1 && (
              <Box sx={cardSx}>
                <Typography sx={sectionHeadingSx}>Contact Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Mobile Number *</Typography>
                    <TextField fullWidth size="small" placeholder="+1 (xxx) xxx-xxxx"
                      {...formik.getFieldProps("driverMobileNum")} sx={fieldSx}
                      error={formik.touched.driverMobileNum && Boolean(formik.errors.driverMobileNum)}
                      helperText={formik.touched.driverMobileNum && formik.errors.driverMobileNum} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Alt Mobile Number</Typography>
                    <TextField fullWidth size="small" placeholder="+1 (xxx) xxx-xxxx"
                      {...formik.getFieldProps("driverAltMobileNum")} sx={fieldSx}
                      error={formik.touched.driverAltMobileNum && Boolean(formik.errors.driverAltMobileNum)}
                      helperText={formik.touched.driverAltMobileNum && formik.errors.driverAltMobileNum} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Email</Typography>
                    <TextField fullWidth size="small" type="email" placeholder="email@example.com"
                      {...formik.getFieldProps("driverEmail")} sx={fieldSx}
                      error={formik.touched.driverEmail && Boolean(formik.errors.driverEmail)}
                      helperText={formik.touched.driverEmail && formik.errors.driverEmail} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>City</Typography>
                    <TextField fullWidth size="small" placeholder="City"
                      {...formik.getFieldProps("driverCity")} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={labelSx}>Address</Typography>
                    <TextField fullWidth size="small" placeholder="Street address"
                      {...formik.getFieldProps("driverAddress")} sx={fieldSx} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ════ TAB 2 — Schedule / Availability ════ */}
            {activeTab === 2 && (
              <Box>
                {/* Stats summary */}
                <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
                  {[
                    { label: "Weekly Hours",         value: `${weeklyStats.hours}h`,                    Icon: AccessTimeIcon,  color: "#2f3891", bg: "#eef2ff" },
                    { label: "Est. Weekly Earnings", value: `$${weeklyStats.earnings.toFixed(2)}`,        Icon: AttachMoneyIcon, color: "#16a34a", bg: "#f0fdf4" },
                  ].map(({ label, value, Icon, color, bg }) => (
                    <Box key={label} sx={{ flex: 1, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color }}>
                        <Icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{value}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>{label}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Day cards */}
                {formik.values.availability.map((day, index) => {
                  const isExpanded = expandedDays.has(day.day);
                  const activeSlots = day.timeSlots.filter((s) => s.startTime || s.endTime);
                  const hasData = activeSlots.length > 0;

                  return (
                    <Box
                      key={day.day}
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        border: `1.5px solid ${hasData ? "#c7d2fe" : "#e2e8f0"}`,
                        mb: 1.5, overflow: "hidden",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                        "&:hover": { boxShadow: "0 2px 12px rgba(99,102,241,0.08)" },
                      }}
                    >
                      {/* Day header row */}
                      <Box
                        onClick={() => toggleDay(day.day)}
                        sx={{
                          display: "flex", alignItems: "center",
                          px: 2.5, py: 1.5, cursor: "pointer",
                          bgcolor: isExpanded ? "#f5f3ff" : "transparent",
                          transition: "background 0.15s",
                          "&:hover": { bgcolor: "#f5f3ff" },
                        }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: hasData ? "#2f3891" : "#cbd5e1", mr: 1.5, flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b", flex: 1 }}>
                          {day.day}
                        </Typography>
                        {hasData && (
                          <Chip
                            label={`${activeSlots.length} slot${activeSlots.length !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#eef2ff", color: "#2f3891", mr: 1.5, border: "1px solid #c7d2fe" }}
                          />
                        )}
                        {!hasData && (
                          <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mr: 1.5, fontStyle: "italic" }}>No slots set</Typography>
                        )}
                        <Box sx={{ color: "#64748b", display: "flex", transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                          <ExpandMoreIcon sx={{ fontSize: 20 }} />
                        </Box>
                      </Box>

                      {/* Expandable slot editor */}
                      <Collapse in={isExpanded}>
                        <Divider sx={{ borderColor: "#e2e8f0" }} />
                        <Box sx={{ p: 2 }}>
                          <FieldArray name={`availability.${index}.timeSlots`}>
                            {({ push, remove }) => (
                              <>
                                {day.timeSlots.map((slot, slotIndex) => (
                                  <Box
                                    key={slotIndex}
                                    sx={{
                                      display: "flex", gap: 1.5, alignItems: "flex-end",
                                      mb: 1.5, bgcolor: "#f8fafc", borderRadius: 2,
                                      p: 1.75, border: "1px solid #f1f5f9",
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ ...labelSx, mb: 0.5 }}>Start Time</Typography>
                                      <TextField fullWidth size="small" type="time"
                                        {...formik.getFieldProps(`availability.${index}.timeSlots.${slotIndex}.startTime`)}
                                        sx={fieldSx} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ ...labelSx, mb: 0.5 }}>End Time</Typography>
                                      <TextField fullWidth size="small" type="time"
                                        {...formik.getFieldProps(`availability.${index}.timeSlots.${slotIndex}.endTime`)}
                                        sx={fieldSx} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ ...labelSx, mb: 0.5 }}>Hourly Rate ($)</Typography>
                                      <TextField fullWidth size="small" type="number" placeholder="0.00"
                                        {...formik.getFieldProps(`availability.${index}.timeSlots.${slotIndex}.hourlySalary`)}
                                        sx={fieldSx} />
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={() => remove(slotIndex)}
                                      sx={{ mb: 0.25, color: "#dc2626", borderRadius: 1.5, "&:hover": { bgcolor: "#fef2f2" } }}
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>
                                ))}

                                <Button
                                  size="small"
                                  startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                                  onClick={() => push({ startTime: "", endTime: "", hourlySalary: "" })}
                                  sx={{
                                    textTransform: "none", fontSize: 12.5, fontWeight: 700,
                                    color: "#2f3891", borderRadius: 2, px: 1.75, py: 0.75,
                                    border: "1.5px dashed #c7d2fe", bgcolor: "#f5f3ff",
                                    "&:hover": { bgcolor: "#eef2ff", borderColor: "#2f3891" },
                                  }}
                                >
                                  Add Time Slot
                                </Button>
                              </>
                            )}
                          </FieldArray>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* ════ TAB 3 — Notes ════ */}
            {activeTab === 3 && (
              <Box sx={cardSx}>
                <Typography sx={sectionHeadingSx}>Internal Notes</Typography>
                <Typography sx={labelSx}>Description</Typography>
                <TextField
                  fullWidth multiline rows={10}
                  placeholder="Add notes, special instructions, or any relevant information about this driver..."
                  {...formik.getFieldProps("driverDescription")}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      alignItems: "flex-start",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </form>
      </FormikProvider>
    </Dialog>
  );
};

export default Driver;
